import { FAKE_ACCOUNT_ID, FAKE_LOGICAL_NOW } from "./fakeRuntime";

export const AUTHORIZATION_OUTCOMES = Object.freeze({
  ALLOW: "allow",
  DENY: "deny",
  REVOKED: "revoked",
  STALE: "stale",
  UNKNOWN: "unknown",
  ACCOUNT_MISMATCH: "account_mismatch",
});

const VALID_OUTCOMES = new Set(Object.values(AUTHORIZATION_OUTCOMES));

export const WORKSPACE_SEMANTIC_ACTIONS = Object.freeze({
  DIALER_OPEN: "workspace.dialer.open",
});

function deepFreeze(value) {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) return value;
  Object.freeze(value);
  Object.values(value).forEach(deepFreeze);
  return value;
}

export function createFakeAuthorizationDecision({
  outcome,
  accountId = FAKE_ACCOUNT_ID,
  action = WORKSPACE_SEMANTIC_ACTIONS.DIALER_OPEN,
  revision = 1,
  decidedAt = FAKE_LOGICAL_NOW,
  freshUntil = FAKE_LOGICAL_NOW + 100,
  decisionId = `fake-auth-decision-${outcome}-001`,
} = {}) {
  if (!VALID_OUTCOMES.has(outcome)) {
    throw new Error(`Unknown authorization outcome: ${outcome}`);
  }
  return deepFreeze({
    decisionId,
    actor: { kind: "fake", reference: "fake-actor-001" },
    accountId,
    action,
    resource: { kind: "fake", reference: "fake-workspace-resource-001" },
    outcome,
    revision,
    decidedAt,
    freshUntil,
  });
}

export const FAKE_AUTHORIZATION_DECISIONS = deepFreeze({
  allow: createFakeAuthorizationDecision({ outcome: AUTHORIZATION_OUTCOMES.ALLOW }),
  deny: createFakeAuthorizationDecision({ outcome: AUTHORIZATION_OUTCOMES.DENY }),
  revoked: createFakeAuthorizationDecision({ outcome: AUTHORIZATION_OUTCOMES.REVOKED }),
  unknown: createFakeAuthorizationDecision({ outcome: AUTHORIZATION_OUTCOMES.UNKNOWN }),
  stale: createFakeAuthorizationDecision({
    outcome: AUTHORIZATION_OUTCOMES.ALLOW,
    decidedAt: FAKE_LOGICAL_NOW - 200,
    freshUntil: FAKE_LOGICAL_NOW - 100,
    decisionId: "fake-auth-decision-stale-001",
  }),
  accountMismatch: createFakeAuthorizationDecision({
    outcome: AUTHORIZATION_OUTCOMES.ALLOW,
    accountId: "fake-other-account-002",
    decisionId: "fake-auth-decision-account-mismatch-001",
  }),
});

/**
 * Evaluates current admission for a semantic action against a fake decision.
 * Account mismatch and unresolved action identity are checked, and thus
 * denied, before the decision's own outcome or freshness is inspected, so
 * a foreign-Account decision never discloses its underlying outcome.
 */
export function evaluateAuthorizationAdmission({ decision, accountId, action, logicalNow }) {
  if (!decision || typeof decision !== "object") {
    return deepFreeze({ admitted: false, outcome: AUTHORIZATION_OUTCOMES.UNKNOWN, reason: "decision_missing" });
  }
  if (decision.accountId !== accountId) {
    return deepFreeze({
      admitted: false,
      outcome: AUTHORIZATION_OUTCOMES.ACCOUNT_MISMATCH,
      reason: "account_mismatch",
    });
  }
  if (decision.action !== action) {
    return deepFreeze({ admitted: false, outcome: AUTHORIZATION_OUTCOMES.UNKNOWN, reason: "action_mismatch" });
  }
  if (decision.outcome === AUTHORIZATION_OUTCOMES.UNKNOWN) {
    return deepFreeze({ admitted: false, outcome: AUTHORIZATION_OUTCOMES.UNKNOWN, reason: "policy_unknown" });
  }
  if (decision.outcome === AUTHORIZATION_OUTCOMES.REVOKED) {
    return deepFreeze({ admitted: false, outcome: AUTHORIZATION_OUTCOMES.REVOKED, reason: "revoked" });
  }
  if (decision.outcome === AUTHORIZATION_OUTCOMES.DENY) {
    return deepFreeze({ admitted: false, outcome: AUTHORIZATION_OUTCOMES.DENY, reason: "denied" });
  }
  if (decision.outcome !== AUTHORIZATION_OUTCOMES.ALLOW) {
    return deepFreeze({ admitted: false, outcome: AUTHORIZATION_OUTCOMES.UNKNOWN, reason: "policy_unknown" });
  }
  if (!Number.isFinite(logicalNow) || !Number.isFinite(decision.freshUntil) || logicalNow > decision.freshUntil) {
    return deepFreeze({ admitted: false, outcome: AUTHORIZATION_OUTCOMES.STALE, reason: "stale" });
  }
  return deepFreeze({ admitted: true, outcome: AUTHORIZATION_OUTCOMES.ALLOW, reason: null });
}

/**
 * Composes bootstrap presentation visibility and current admission as two
 * independently derived facts. Neither field may be inferred from the
 * other, and a foreign-Account projection never discloses its visibility.
 */
export function composeAuthorizationPresentation({
  authorizationProjection,
  decision,
  accountId,
  action,
  logicalNow,
}) {
  const admission = evaluateAuthorizationAdmission({ decision, accountId, action, logicalNow });
  const projectionAccountId = authorizationProjection?.accountId;
  const visibility =
    projectionAccountId && projectionAccountId === accountId
      ? (authorizationProjection?.data?.visibility ?? "unknown")
      : "unknown";
  return deepFreeze({ visibility, admission });
}

/**
 * Captures an attempted semantic action without executing it. The intent
 * retains the decision revision it relied upon so a later revision change
 * or revocation can be detected on revalidation.
 */
export function captureSemanticIntent({ action, resource, decision, logicalNow }) {
  if (!decision || typeof decision !== "object") {
    throw new Error("captureSemanticIntent requires a decision to correlate against");
  }
  return deepFreeze({
    intentId: `fake-intent-${action}-${decision.decisionId}-${logicalNow}`,
    action,
    resource: resource ?? decision.resource,
    accountId: decision.accountId,
    decisionId: decision.decisionId,
    decisionRevision: decision.revision,
    capturedAt: logicalNow,
    executed: false,
  });
}

/**
 * Revalidates a previously captured intent against the current decision.
 * A changed revision, a changed decision identity, or a current Account
 * that no longer matches the intent's Account all fail closed without
 * reusing the original admission.
 */
export function revalidateCapturedIntent({ intent, currentDecision, accountId, logicalNow }) {
  if (!intent || typeof intent !== "object") {
    return deepFreeze({ admitted: false, outcome: AUTHORIZATION_OUTCOMES.UNKNOWN, reason: "intent_missing" });
  }
  if (intent.accountId !== accountId) {
    return deepFreeze({
      admitted: false,
      outcome: AUTHORIZATION_OUTCOMES.ACCOUNT_MISMATCH,
      reason: "account_mismatch",
    });
  }
  if (!currentDecision || currentDecision.accountId !== accountId) {
    return deepFreeze({
      admitted: false,
      outcome: AUTHORIZATION_OUTCOMES.ACCOUNT_MISMATCH,
      reason: "account_mismatch",
    });
  }
  if (currentDecision.decisionId !== intent.decisionId || currentDecision.revision !== intent.decisionRevision) {
    return deepFreeze({
      admitted: false,
      outcome: AUTHORIZATION_OUTCOMES.STALE,
      reason: "decision_revision_changed",
    });
  }
  return evaluateAuthorizationAdmission({
    decision: currentDecision,
    accountId,
    action: intent.action,
    logicalNow,
  });
}
